import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Grid, IconButton, CircularProgress, Alert,
  Container, Stack, Chip, Collapse, Divider, Button, Tooltip, Pagination, Link
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from '@mui/icons-material/Cancel';
import { downloadProjectPDF } from './generateProjectsPDF';
import { OrganizationsupdateCompanyProject } from "../../../helper/base_url";

const ITEMS_PER_PAGE = 3;

const getStatusColor = (status) => {
  switch (status) {
    case 'Active': return '#4caf50';
    case 'Pending': return '#ff9800';
    case 'Closed': return '#f44336';
    default: return '#757575';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Active': return <CheckCircleIcon fontSize="small" />;
    case 'Pending': return <HourglassEmptyIcon fontSize="small" />;
    case 'Closed': return <CancelIcon fontSize="small" />;
    default: return null;
  }
};

const ActiveProjects = () => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expandedProjects, setExpandedProjects] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      const token = localStorage.getItem('company_token');
      try {
        const response = await axios.get(`${OrganizationsupdateCompanyProject}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activeProjects = response.data.data.filter(p => p.status === 'Active');
        setProjects(activeProjects);
      } catch (err) {
        setError(err.response?.data?.error || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handlePageChange = (_, value) => setPage(value);

  const toggleExpand = (id) => {
    setExpandedProjects((prev) =>
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const handleDownloadPDF = (project) => downloadProjectPDF(project);

  const paginatedProjects = projects.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  if (loading)
    return (
      <Container sx={{ textAlign: 'center', marginTop : 8 }}>
        <CircularProgress />
      </Container>
    );

  if (error)
    return (
     <Box textAlign="center" paddingTop={10} mt={6}>
      <Typography variant="h6" color="textSecondary">
        No Active Projects yet.
      </Typography>
    </Box>
    );

  return (
    <Box
      sx={{
        inlineSize: '100%',
        backgroundColor: '#f4f6f8',
        padding: "80px 20px 20px 20px",
        blockSize: '100%',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      <Typography variant="h6" gutterBottom textAlign="start" fontWeight={400} marginBottom="20px">
        My Active Projects
      </Typography>

      <Grid container spacing={4}>
        {paginatedProjects.map((project, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                width : 600,
                borderRadius: 4,
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                backgroundColor: '#ffffff',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Chip
                    icon={getStatusIcon(project.status)}
                    label={project.status}
                    sx={{
                      backgroundColor: getStatusColor(project.status),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                    }}
                  />
                   <Stack direction="row" spacing={1}>
                    <Tooltip title="Download PDF">
                      <IconButton onClick={() => handleDownloadPDF(project)}><DownloadIcon color="primary" /></IconButton>
                    </Tooltip>
                    </Stack>
                </Stack>

                <Typography variant="body1" fontWeight={500}><strong>Project Title:</strong> {project.projectTitle}</Typography>
                <Typography variant="subtitle1" mt={1} mb={2} color="text.secondary">
                  {project.projectSummary}
                </Typography>

                <Stack spacing={1.5} mb={2}>
                  <Typography display="flex" alignItems="center" fontWeight={600}>
                    <WorkIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    {project.companyName}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {project.companyOverview}
                  </Typography>

                  {project.websiteOrLinkedIn && (
                    <Typography variant="body2">
                      <Link href={project.websiteOrLinkedIn} target="_blank" rel="noopener" underline="hover">
                        {project.websiteOrLinkedIn}
                      </Link>
                    </Typography>
                  )}
                </Stack>

                <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
                  {project.domain && (
                    <Chip label={project.domain} variant="outlined" color="primary" />
                  )}
                  {project.preferredTechStack && (
                    <Chip label={project.preferredTechStack} variant="outlined" color="secondary" />
                  )}
                  {project.preferredProgrammingLanguages && (
                    <Chip label={project.preferredProgrammingLanguages} variant="outlined" />
                  )}
                </Stack>

                <Collapse in={expandedProjects.includes(project._id)}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600}>Project Details</Typography>
                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Deliverables:</strong> {project.expectedDeliverables}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Duration:</strong> {project.projectDuration}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Time/Week:</strong> {project.weeklyTimeCommitment}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Mode:</strong> {project.modeOfWork}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Compensation:</strong> {project.compensation}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Confidentiality:</strong> {project.confidentialityOrIPClauses}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2"><CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} /><strong>Start:</strong> {new Date(project.desiredStartDate).toLocaleDateString()}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2"><CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} /><strong>Deadline:</strong> {new Date(project.applicationDeadline).toLocaleDateString()}</Typography></Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" fontWeight={600}>Point of Contact</Typography>
                  <Typography variant="body2">{project.pointOfContact?.name} – {project.pointOfContact?.designation}</Typography>
                  <Typography variant="body2">Email: {project.pointOfContact?.email}</Typography>
                  <Typography variant="body2">Phone: {project.pointOfContact?.phoneNumber}</Typography>

                  {project.s3Url && (
                    <Typography variant="body2" mt={2}>
                      <strong>📎 Attachment:</strong> <Link href={project.s3Url} target="_blank" rel="noopener">Download</Link>
                    </Typography>
                  )}
                </Collapse>
                 <Button
                    size="small"
                    variant="outlined"
                    sx={{ color: '#1976d2', textTransform: 'none' }}
                    onClick={() => toggleExpand(project._id)}
                  >
                    {expandedProjects.includes(project._id) ? 'View Less' : 'View More'}
                  </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={6} display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(projects.length / ITEMS_PER_PAGE)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
        />
      </Box>
    </Box>
  );
};

export default ActiveProjects;
